import _ from 'lodash';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Flex } from 'reflexbox';
import { motion } from 'framer-motion';

import { AddIcon } from '@treams/treams-icons';
import { IconButton, Modal, Popover } from '@treams/treams-ui';

import * as graphql from 'generated/graphql';
import { FeedbackLibrary } from './FeedbackLibrary';

const PlaceholderContainer = styled(Flex)`
	border: 1px dashed ${(props) => props.theme.colors.lightBlueDark};
	min-height: 94px;
	border-radius: 5px;
	cursor: pointer;
	transition:
		0.2s ease-in-out border-color,
		0.2s ease-in-out background-color;

	&:hover {
		border-color: transparent;
		background: ${(props) => props.theme.colors.lightBlueLight};

		&:active {
			background: ${(props) => props.theme.colors.lightBlue};
		}
	}
`;

export function QuestionSelector(props: {
	readOnly?: boolean;
	onCancel?: () => void;
	onSelect?: (questions: graphql.FeedbackQuestion[]) => void;
}) {
	const { t } = useTranslation();
	const [selected, setSelected] = useState<graphql.FeedbackQuestion[]>([]);

	return (
		<Modal>
			<Modal.Header title={t('feedback.settings.template.questionSelector.title')} />
			<Modal.Contents>
				<FeedbackLibrary
					inline
					readOnly={props.readOnly}
					selectedQuestions={selected}
					onChangeSelectedQuestions={setSelected}
				/>
			</Modal.Contents>
			<Modal.FormFooter
				onCancel={props.onCancel}
				onSave={() => props.onSelect?.(_.sortBy(selected, 'order'))}
				saveTitle={t('feedback.settings.template.questionSelector.add')}
			/>
		</Modal>
	);
}

export default function AddFeedbackQuestion(props: {
	onSelect?: (questions: graphql.FeedbackQuestion[]) => void;
}) {
	const [visible, setVisible] = useState(false);

	return (
		<motion.div
			layout="position"
			layoutId="template-question-placeholder"
			transition={{ duration: 0.2, ease: 'circOut' }}
		>
			<PlaceholderContainer
				alignItems="center"
				justifyContent="center"
				ml={80}
				onClick={() => setVisible(true)}
			>
				<Popover.Target
					center
					visible={visible}
					contents={
						<QuestionSelector
							onCancel={() => setVisible(false)}
							onSelect={(questions) => {
								props.onSelect?.(questions);
								setVisible(false);
							}}
						/>
					}
				>
					<IconButton type="secondary" icon={<AddIcon />} />
				</Popover.Target>
			</PlaceholderContainer>
		</motion.div>
	);
}
